/**
 * PeriodeDoelCard — omzetdoel-gauge voor de lopende periode (tab Overzicht).
 *
 * Volgt de maand/kwartaal/jaar-schakelaar maar toont altijd de LOPENDE
 * periode van die grain (pace en verwacht einde zijn voor een afgesloten of
 * toekomstige periode niet zinvol). Links een halve-cirkel-gauge: gefactureerd
 * t.o.v. het periodedoel, met pace-marker ("hier hoor je vandaag te staan")
 * en een lichte boog voor het verwachte periode-einde (gefactureerd +
 * ingeplande afleveringen). Rechts de cumulatieve voortgang met rechte
 * doellijn en gestippeld verwacht-pad.
 *
 * Data: GET /api/kpi/daily (gefactureerd + gepland per dag, mirror_invoice
 * excl. btw) + GET/PUT /api/kpi/targets (doel per grain, carry-forward).
 * Dag-semantiek is UTC, identiek aan /api/kpi/daily.
 *
 * De gauge is hand-getekende SVG (pace-marker en verwacht-boog zijn in een
 * Recharts-Pie niet te positioneren); de voortgangsgrafiek is Recharts.
 *
 * Plan: plans/2026-07-13-cijfers-omzetdoel-gauge-en-m2-ingepakt.md.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  useCijfersStore,
  currentPeriod,
  formatPeriodLabel,
  type DailyOmzet,
  type Grain,
  type TargetInfo,
} from '../../stores/cijfers-store';

const nlEur0 = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

function compactEur(value: number): string {
  if (Math.abs(value) >= 10_000) return `€ ${Math.round(value / 1000)}k`;
  return `€ ${value.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`;
}

const GRAIN_TITLE: Record<Grain, string> = {
  month: 'Maanddoel',
  quarter: 'Kwartaaldoel',
  year: 'Jaardoel',
};

const GRAIN_EINDE: Record<Grain, string> = {
  month: 'maandeinde',
  quarter: 'kwartaaleinde',
  year: 'jaareinde',
};

interface PeriodContext {
  grain: Grain;
  period: string; // '2026-07' | '2026-Q3' | '2026'
  todayIso: string;
  startIso: string; // eerste dag van de lopende periode
  endIso: string; // laatste dag van de lopende periode
  dayOfPeriod: number; // 1-based
  daysInPeriod: number;
}

function isoOf(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** UTC-context van de lopende periode, consistent met /api/kpi/daily. */
function periodContext(grain: Grain): PeriodContext {
  const todayIso = new Date().toISOString().slice(0, 10);
  const year = Number(todayIso.slice(0, 4));
  const month = Number(todayIso.slice(5, 7));

  let start: Date;
  let endExclusive: Date;
  if (grain === 'month') {
    start = new Date(Date.UTC(year, month - 1, 1));
    endExclusive = new Date(Date.UTC(year, month, 1));
  } else if (grain === 'quarter') {
    const qMonth = Math.floor((month - 1) / 3) * 3;
    start = new Date(Date.UTC(year, qMonth, 1));
    endExclusive = new Date(Date.UTC(year, qMonth + 3, 1));
  } else {
    start = new Date(Date.UTC(year, 0, 1));
    endExclusive = new Date(Date.UTC(year + 1, 0, 1));
  }

  const MS_DAY = 86_400_000;
  const daysInPeriod = Math.round((endExclusive.getTime() - start.getTime()) / MS_DAY);
  const dayOfPeriod =
    Math.round((new Date(`${todayIso}T00:00:00Z`).getTime() - start.getTime()) / MS_DAY) + 1;
  const end = new Date(endExclusive.getTime() - MS_DAY);

  return {
    grain,
    period: currentPeriod(grain),
    todayIso,
    startIso: isoOf(start),
    endIso: isoOf(end),
    dayOfPeriod,
    daysInPeriod,
  };
}

// ─── Gauge (SVG halve cirkel) ───────────────────────────────────

const GAUGE_R = 80;
const GAUGE_CX = 100;
const GAUGE_CY = 98;

function gaugePoint(frac: number, r: number = GAUGE_R): { x: number; y: number } {
  const angle = Math.PI * Math.min(1, Math.max(0, frac));
  return { x: GAUGE_CX - r * Math.cos(angle), y: GAUGE_CY - r * Math.sin(angle) };
}

function arcPath(fromFrac: number, toFrac: number): string {
  const a = gaugePoint(fromFrac);
  const b = gaugePoint(toFrac);
  return `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} A ${GAUGE_R} ${GAUGE_R} 0 0 1 ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
}

function Gauge({
  mtd,
  target,
  expected,
  paceFrac,
  color,
}: {
  mtd: number;
  target: number;
  expected: number;
  paceFrac: number;
  color: string;
}) {
  const mtdFrac = Math.min(1, mtd / target);
  const expectedFrac = Math.min(1, expected / target);
  const paceInner = gaugePoint(paceFrac, GAUGE_R - 13);
  const paceOuter = gaugePoint(paceFrac, GAUGE_R + 11);

  return (
    <svg viewBox="0 0 200 108" className="w-full max-w-xs">
      {/* achtergrond */}
      <path d={arcPath(0, 1)} fill="none" stroke="#e5e7eb" strokeWidth={14} />
      {/* verwacht periode-einde (lichte boog) */}
      {expectedFrac > mtdFrac && (
        <path d={arcPath(0, expectedFrac)} fill="none" stroke={color} strokeOpacity={0.25} strokeWidth={14} />
      )}
      {/* gefactureerd deze periode */}
      {mtdFrac > 0 && <path d={arcPath(0, mtdFrac)} fill="none" stroke={color} strokeWidth={14} />}
      {/* pace-marker: hier hoor je vandaag te staan */}
      <line
        x1={paceInner.x}
        y1={paceInner.y}
        x2={paceOuter.x}
        y2={paceOuter.y}
        stroke="#374151"
        strokeWidth={2.5}
      />
      <text x={GAUGE_CX} y={78} textAnchor="middle" className="fill-gray-900" fontSize={19} fontWeight={600}>
        {nlEur0.format(mtd)}
      </text>
      <text x={GAUGE_CX} y={96} textAnchor="middle" className="fill-gray-500" fontSize={9.5}>
        doel {compactEur(target)} · verwacht {compactEur(expected)}
      </text>
    </svg>
  );
}

// ─── Periodevoortgang (cumulatief) ──────────────────────────────

interface ProgressRow {
  label: string;
  cum: number | null;
  doel: number;
  verwacht: number | null;
}

const PROGRESS_LABELS: Record<string, string> = {
  cum: 'Gefactureerd (cumulatief)',
  doel: 'Doellijn',
  verwacht: 'Verwacht',
};

function buildProgressRows(
  daily: DailyOmzet,
  target: number,
  ctx: PeriodContext
): { rows: ProgressRow[]; realized: number; expected: number; todayLabel: string } {
  const byDay = new Map(daily.days.map((d) => [d.day, d]));
  const rows: ProgressRow[] = [];
  let cum = 0;
  let verwacht: number | null = null;
  let todayLabel = '';
  const start = new Date(`${ctx.startIso}T00:00:00Z`);
  for (let i = 1; i <= ctx.daysInPeriod; i++) {
    const date = new Date(start.getTime() + (i - 1) * 86_400_000);
    const dayIso = isoOf(date);
    const d = byDay.get(dayIso);
    const isPast = dayIso <= ctx.todayIso;
    if (isPast) cum += d?.gefactureerd ?? 0;
    if (dayIso === ctx.todayIso) verwacht = cum;
    else if (dayIso > ctx.todayIso && verwacht !== null) verwacht += d?.gepland ?? 0;
    // Maand: dagnummer; kwartaal/jaar: d/m (compact, minTickGap dunt uit).
    const label =
      ctx.grain === 'month' ? String(i) : `${date.getUTCDate()}/${date.getUTCMonth() + 1}`;
    if (dayIso === ctx.todayIso) todayLabel = label;
    rows.push({
      label,
      cum: isPast ? Math.round(cum) : null,
      doel: Math.round((target * i) / ctx.daysInPeriod),
      verwacht: dayIso >= ctx.todayIso && verwacht !== null ? Math.round(verwacht) : null,
    });
  }
  const realized = rows.reduce((acc, r) => (r.cum !== null ? r.cum : acc), 0);
  const expected = rows.reduce((acc, r) => (r.verwacht !== null ? r.verwacht : acc), realized);
  return { rows, realized, expected, todayLabel };
}

// ─── Doel bewerken ──────────────────────────────────────────────

function TargetEditor({
  current,
  grain,
  onSave,
  onCancel,
}: {
  current: number | null;
  grain: Grain;
  onSave: (value: number) => Promise<void>;
  onCancel: () => void;
}) {
  const [raw, setRaw] = useState(current !== null ? String(current) : '');
  const [busy, setBusy] = useState(false);
  const value = Number(raw);
  const valid = Number.isFinite(value) && value > 0;

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={1}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder={`${GRAIN_TITLE[grain]} in €`}
        className="w-32 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
        autoFocus
      />
      <button
        disabled={!valid || busy}
        onClick={async () => {
          setBusy(true);
          await onSave(value);
          setBusy(false);
        }}
        className="px-2.5 py-1 text-xs rounded-lg bg-indigo-600 text-white disabled:opacity-40"
      >
        {busy ? 'Opslaan…' : 'Opslaan'}
      </button>
      <button onClick={onCancel} className="px-2 py-1 text-xs text-gray-500 hover:text-main">
        Annuleren
      </button>
    </div>
  );
}

// ─── Card ───────────────────────────────────────────────────────

export function PeriodeDoelCard() {
  const grain = useCijfersStore((s) => s.grain);
  const loadDaily = useCijfersStore((s) => s.loadDaily);
  const loadTarget = useCijfersStore((s) => s.loadTarget);
  const saveTarget = useCijfersStore((s) => s.saveTarget);
  const [daily, setDaily] = useState<DailyOmzet | null>(null);
  const [targetInfo, setTargetInfo] = useState<TargetInfo | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);

  const ctx = useMemo(() => periodContext(grain), [grain]);

  useEffect(() => {
    let active = true;
    setLoaded(false);
    const back = Math.max(1, ctx.dayOfPeriod - 1);
    const forward = ctx.daysInPeriod - ctx.dayOfPeriod;
    void Promise.all([
      loadDaily(back, forward),
      loadTarget('omzet_uitvoering', ctx.grain, ctx.period),
    ]).then(([d, t]) => {
      if (!active) return;
      setDaily(d);
      setTargetInfo(t);
      setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [ctx, loadDaily, loadTarget]);

  const target = targetInfo?.target ?? null;
  const computed = useMemo(
    () => (daily && target !== null ? buildProgressRows(daily, target, ctx) : null),
    [daily, target, ctx]
  );

  // Gefactureerd ook zonder doel kunnen tonen (lege-staat).
  const realizedOnly = useMemo(() => {
    if (!daily) return 0;
    return Math.round(
      daily.days
        .filter((d) => d.day >= ctx.startIso && d.day <= ctx.todayIso)
        .reduce((acc, d) => acc + d.gefactureerd, 0)
    );
  }, [daily, ctx]);

  const handleSave = async (value: number) => {
    const ok = await saveTarget('omzet_uitvoering', ctx.grain, ctx.period, value);
    if (ok) {
      const fresh = await loadTarget('omzet_uitvoering', ctx.grain, ctx.period);
      setTargetInfo(fresh);
      setEditing(false);
    }
  };

  const paceFrac = ctx.dayOfPeriod / ctx.daysInPeriod;
  // Kleur t.o.v. pace: groen >= 95% van het pro-rata-doel, oranje 80-95%, rood < 80%.
  const paceRatio = computed && target ? computed.realized / (target * paceFrac) : 0;
  const color = paceRatio >= 0.95 ? '#10b981' : paceRatio >= 0.8 ? '#f59e0b' : '#ef4444';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div>
          <h3 className="text-sm font-medium text-main">
            {GRAIN_TITLE[ctx.grain]} omzet — {formatPeriodLabel(ctx.period, ctx.grain)}
          </h3>
          <p className="text-xs text-gray-400">
            Gefactureerd deze periode t.o.v. het doel (excl. btw). Streepje = waar je vandaag hoort te staan; licht = verwacht {GRAIN_EINDE[ctx.grain]} incl. ingeplande afleveringen.
          </p>
        </div>
        {editing ? (
          <TargetEditor current={target} grain={ctx.grain} onSave={handleSave} onCancel={() => setEditing(false)} />
        ) : (
          <button
            onClick={() => setEditing(true)}
            title={`${GRAIN_TITLE[ctx.grain]} wijzigen`}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg text-gray-500 hover:text-main hover:bg-gray-50"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            </svg>
            {target !== null ? 'Doel wijzigen' : 'Doel instellen'}
          </button>
        )}
      </div>

      {!loaded ? (
        <div className="h-52 flex items-center justify-center text-sm text-gray-400">Laden…</div>
      ) : target === null || !computed ? (
        <div className="h-52 flex flex-col items-center justify-center gap-1 text-sm text-gray-400">
          <span className="text-2xl font-semibold text-main">{nlEur0.format(realizedOnly)}</span>
          <span>gefactureerd deze periode — geen doel ingesteld</span>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 items-center">
          <div className="flex justify-center">
            <Gauge
              mtd={computed.realized}
              target={target}
              expected={computed.expected}
              paceFrac={paceFrac}
              color={color}
            />
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <ComposedChart data={computed.rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: '#6b7280', fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                minTickGap={16}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={46}
                tickFormatter={compactEur}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}
                labelFormatter={(label) => (ctx.grain === 'month' ? `dag ${label}` : String(label))}
                formatter={(value: number, name: string) => [nlEur0.format(value), PROGRESS_LABELS[name] ?? name]}
              />
              {computed.todayLabel && (
                <ReferenceLine
                  x={computed.todayLabel}
                  stroke="#9ca3af"
                  strokeDasharray="4 3"
                  label={{ value: 'vandaag', position: 'top', fill: '#9ca3af', fontSize: 10 }}
                />
              )}
              <Line type="linear" dataKey="doel" stroke="#9ca3af" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              <Line
                type="monotone"
                dataKey="cum"
                stroke={color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="verwacht"
                stroke={color}
                strokeWidth={1.5}
                strokeDasharray="5 4"
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
